import React, { useEffect, useState } from 'react';
import { Table, TextInput, rem, createStyles, Text, Center, Pagination, Flex, ScrollArea, Button, Modal, useMantineTheme, Skeleton, Title, Box, Image, Portal, Overlay, Loader } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconSearch, IconCheck } from '@tabler/icons-react';
import { keys } from '@mantine/utils';
import PostForm from '../forms/PostForm';
import { getPosts, deletePost } from '../../services/api.service';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import { modals } from '@mantine/modals';
import DeleteButton from '../buttons/DeleteButton';
import { useSearchParams } from 'react-router-dom';

// Custom styles
const useStyles = createStyles((theme) => ({
	container: {
		padding: theme.spacing.md,
		height: '100%',
	},
	th: {
		padding: '0 !important',
	},
	control: {
		width: '100%',
		padding: `${theme.spacing.xs} ${theme.spacing.md}`,
		'&:hover': {
			backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[1],
		},
	},
	icon: {
		width: rem(21),
		height: rem(21),
		borderRadius: rem(21),
	},
	imagePreview: {
		padding: 3,
		border: `1px solid ${theme.colorScheme === 'dark' ? theme.white : theme.black}`,
		borderRadius: theme.radius.sm,
		backgroundColor: theme.colorScheme === 'dark' ? theme.white : theme.black,
	}
}));

const Posts = () => {

	// State and hooks
	const [isLoading, setIsLoading] = useState(true);
	const [search, setSearch] = useState('');
	const [posts, setPosts] = useState([]);
	const [isEdit, setIsEdit] = useState(false);
	const [postEdit, setPostEdit] = useState({});
	const [filteredData, setFilteredData] = useState([]);
	const [opened, { open, close }] = useDisclosure(false);
	const theme = useMantineTheme();
	const { classes } = useStyles();
	const [isDeleteLoading, toggleDeleteLoading] = useState(false);
	const [searchParams] = useSearchParams();

	// On load
	useEffect(() => {

		// Get list
		getPostsList();

		// Check query params to open form
		if (searchParams.get('add') === '1') open();

		// eslint-disable-next-line
	}, []);

	// Get posts
	const getPostsList = () => {

		// Get
		getPosts(function (response) {

			// Set posts and initial sorted data, remove loader
			setPosts(response.data);
			setFilteredData(response.data);
			setIsLoading(false);

		}, function (error) {

			// Remove loader
			setIsLoading(false);

			// Show error
			notifications.show({
				title: 'Error',
				message: error,
				color: 'red'
			});

		});

	}

	// TH component
	const Th = ({ children, width }) => {
		const { classes } = useStyles();
		return (
			<th className={classes.th} width={width}>
				<Text
					fw={700}
					fz="sm"
					className={classes.control}
				>
					{children}
				</Text>
			</th>
		);
	}

	// Build table head
	const ths = (
		<tr>
			<Th>
				ID
			</Th>
			<Th>
				Título
			</Th>
			<Th>
				Inicio
			</Th>
			<Th>
				Finalización
			</Th>
			<Th>
				Imagen
			</Th>
			<Th>
				Categoría
			</Th>
			<Th></Th>
		</tr>
	);

	// Build rows
	const rows = filteredData.map((element) => (
		<tr
			style={{ cursor: 'pointer' }}
			key={element.id}
			onClick={(event) => togglePostEdit(event, element)}
		>
			<td width={100}>
				{element.id}
			</td>
			<td>
				{element.title}
			</td>
			<td>
				{dayjs(new Date(element.startDate)).format('DD-MM-YYYY HH:mm')}
			</td>
			<td>
				{dayjs(new Date(element.endDate)).format('DD-MM-YYYY HH:mm')}
			</td>
			<td>
				<Image
					src={element.image}
					className={classes.imagePreview}
					radius={theme.radius.sm}
					maw={100}
				/>
			</td>
			<td>
				{element.postCategory ? `(${element.postCategory.id}) ${element.postCategory.name}` : ''}
			</td>
			<td>
				<DeleteButton
					onClick={(e) => {
						e.stopPropagation();
						openDeleteModal(element.id);
					}}
				/>
			</td>
		</tr>
	));

	// Handle search change
	const handleSearchChange = (event) => {
		const { value } = event.currentTarget;
		setSearch(value);
		filterData(posts, value);
	};

	// Filter and return from query/search/sort
	const filterData = (data, search) => {
		const query = search.toLowerCase().trim();
		setFilteredData(data.filter((item) => keys(data[0]).some((key) => item[key].toString().toLowerCase().includes(query))));
	}

	// Close posts modal
	const closeModal = () => {
		// Get posts and close modal
		getPostsList();
		setIsEdit(false);
		setPostEdit({});
		close();
	}

	// Toggle post edit
	const togglePostEdit = (event, post) => {
		// Stop click and toggle edit
		event.stopPropagation();
		setIsEdit(true);
		setPostEdit(post);
		// Open modal
		open();
	}

	// Open delete confirmation modal
	const openDeleteModal = (id) =>
		modals.openConfirmModal({
			title: 'Eliminar Noticia',
			centered: true,
			children: (
				<Text size="sm">
					¿Estás seguro de querer borrar esta noticia? Esta acción no se puede deshacer.
				</Text>
			),
			labels: { confirm: 'Eliminar noticia', cancel: 'Cancelar' },
			confirmProps: { color: 'red', variant: 'light' },
			onCancel: () => { }, // Nothing
			onConfirm: () => triggerDeletePost(id),
			closeOnClickOutside: false,
			zIndex: 400
		});

	// Trigger delete post
	const triggerDeletePost = (id) => {

		// Set loader
		toggleDeleteLoading(true);

		// Call API
		deletePost(id, function () {

			// Show success
			notifications.show({
				title: 'Noticia eliminada correctamente',
				color: 'green',
				icon: <IconCheck />
			});

			// Close after 1 second
			setTimeout(() => {

				// Remove preloader
				toggleDeleteLoading(false);

				// Close modal
				closeModal();

			}, 1000);

		}, function (error) {

			// Remove preloader
			toggleDeleteLoading(false);

			// Show error
			notifications.show({
				title: 'Error',
				message: error,
				color: 'red'
			});

		});

	}

	return (
		<>
			{/* Modal */}
			<Modal
				opened={opened}
				withCloseButton={false}
				centered
				size="xl"
				overlayProps={{
					color: theme.colorScheme === 'dark' ? theme.colors.dark[9] : theme.colors.gray[2],
					opacity: 0.55,
					blur: 3,
				}}
				closeOnClickOutside={false}
				closeOnEscape={false}
			>
				{/* New Post Form */}
				<PostForm
					closeModal={closeModal}
					isEdit={isEdit}
					post={postEdit}
				/>
			</Modal>
			{/* Delete Loader */}
			{isDeleteLoading ?
				<Portal>
					<Overlay
						sx={{ zIndex: 999 }}
						center
						blur={15}
					>
						<Loader size={100} />
					</Overlay>
				</Portal>
			: null}
			{/* Content */}
			<Box className="mainContainer">
				{/* Content */}
				<Flex
					direction="column"
					justify="space-between"
					className={classes.container}
				>
					{/* Title */}
					<Title mb="xl">
						Noticias
					</Title>
					{/* Group */}
					<Flex direction="column">
						{/* Top */}
						<Flex mb="md">
							{/* New Post */}
							<Button
								onClick={open}
								variant="filled"
							>
								Nueva Noticia
							</Button>
							{/* Search */}
							<TextInput
								placeholder="Buscar noticias"
								icon={<IconSearch size="0.9rem" stroke={1.5} />}
								value={search}
								onChange={handleSearchChange}
								ml="sm"
								sx={{
									width: '100%',
									maxWidth: 350
								}}
								variant="filled"
							/>
						</Flex>
						{/* Table */}
						<ScrollArea>
							{isLoading ?
								<>
									<Skeleton height={40} radius="sm" />
									<Skeleton height={32} mt={12} radius="sm" />
									<Skeleton height={32} mt={12} radius="sm" />
									<Skeleton height={32} mt={12} radius="sm" />
									<Skeleton height={32} mt={12} radius="sm" />
								</>
								:
								<Table
									highlightOnHover
									sx={{ flex: 2 }}
								>
									<thead>{ths}</thead>
									<tbody>{rows.length > 0 ? (
										rows
									) : (
										<tr>
											<td colSpan={3}>
												<Text
													weight={500}
													align="center"
													my="xl"
													color="dimmed"
												>
													Sin resultados
												</Text>
											</td>
										</tr>
									)}</tbody>
								</Table>
							}
						</ScrollArea>
					</Flex>
					{/* Pages */}
					<Center sx={{ marginTop: 20 }}>
						<Pagination
							total={1}
							siblings={1}
							defaultValue={1}
							withEdges
						/>
					</Center>
				</Flex>
			</Box>
		</>
	);

}

export default Posts;
